import { Fragment, useEffect, useMemo, useState } from "react";
import {
  adminMediaUrl,
  clearAdminSession,
  getQuoteToolConfig,
  isAdminAuthenticated,
  saveQuoteToolPricing,
  uploadQuoteToolItemImage,
} from "../services/adminApi";
import { buildSections } from "../../pages/quotationToolData";
import "./CataloguePage.css";
import "./QuotePricingPage.css";

const SECTIONS = [
  ["packages", "Package rates"],
  ["room-amounts", "Room removal amounts"],
  ["entrance", "Entrance"],
  ["living", "Living"],
  ["pooja", "Pooja"],
  ["dining", "Dining"],
  ["kitchen", "Kitchen"],
  ["bed1", "Bedroom 1"],
  ["bed2", "Bedroom 2"],
  ["bed3", "Bedroom 3"],
  ["bed4", "Bedroom 4"],
  ["bed5", "Bedroom 5"],
  ["bath", "Bathroom"],
  ["study", "Study"],
  ["utility", "Utility"],
  ["passage", "Passage"],
  ["balcony", "Balcony"],
  ["staircase", "Staircase"],
  ["civil", "Civil"],
  ["finishes", "Finishes"],
  ["electrical", "Electrical"],
  ["extras", "Extras"],
  ["handover", "Handover"],
  ["settings", "Quotation settings"],
];
const ROOM_SECTIONS = SECTIONS.filter(([key]) => !["packages", "room-amounts", "settings"].includes(key));
const ROOM_AMOUNT_KEY = (sectionKey, packageCode) => `room_amount.${sectionKey}.${packageCode}`;
// Existing records with no CRM override continue to use the inclusion defined by the tool.
const DEFAULT_INCLUDED_BY_ITEM = Object.fromEntries(
  buildSections(5, true).flatMap((section) =>
    section.items.map((item) => [`${section.id}.${item.id}`, item.inc]),
  ),
);

const UNITS = [
  ["SQ.FT", "Square feet (SQ.FT)"],
  ["RFT", "Running feet (RFT)"],
  ["FT", "Feet (FT)"],
  ["MTR", "Metre (MTR)"],
  ["EACH", "Each (EACH)"],
  ["NO", "Per item (NO)"],
  ["SET", "Per set (SET)"],
  ["APPROX", "Approximate item (APPROX)"],
];
const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
const unitLabel = (unit) =>
  UNITS.find(([value]) => value === String(unit || "").toUpperCase())?.[1] ||
  unit ||
  "Each (EACH)";

export default function QuotePricingPage() {
  const [config, setConfig] = useState({ packages: [], items: [], settings: {} });
  const [activeSection, setActiveSection] = useState("packages");
  const [dirtyPackages, setDirtyPackages] = useState(new Set());
  const [dirtyItems, setDirtyItems] = useState(new Set());
  const [dirtyRoomAmounts, setDirtyRoomAmounts] = useState(new Set());
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingItemKey, setUploadingItemKey] = useState(null);
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setConfig(await getQuoteToolConfig());
      setDirtyPackages(new Set());
      setDirtyItems(new Set());
      setDirtyRoomAmounts(new Set());
      setSettingsDirty(false);
    } catch (error) {
      setMessage(error.message || "Unable to load quotation tool pricing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdminAuthenticated()) window.location.replace("/admin/login");
    else load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sectionItems = useMemo(
    () => config.items.filter((item) => item.section_key === activeSection),
    [config.items, activeSection],
  );

  const changeCount = dirtyPackages.size + dirtyItems.size + dirtyRoomAmounts.size + (settingsDirty ? 1 : 0);

  const updatePackage = (code, key, value) => {
    setConfig((current) => ({
      ...current,
      packages: current.packages.map((pkg) =>
        pkg.code === code ? { ...pkg, [key]: value } : pkg,
      ),
    }));
    setDirtyPackages((current) => new Set([...current, code]));
  };

  const updateSpec = (code, index, position, value) => {
    setConfig((current) => ({
      ...current,
      packages: current.packages.map((pkg) =>
        pkg.code === code
          ? {
              ...pkg,
              specs: pkg.specs.map((pair, pairIndex) =>
                pairIndex === index
                  ? pair.map((cell, cellIndex) => (cellIndex === position ? value : cell))
                  : pair,
              ),
            }
          : pkg,
      ),
    }));
    setDirtyPackages((current) => new Set([...current, code]));
  };

  const addSpec = (code) => {
    setConfig((current) => ({
      ...current,
      packages: current.packages.map((pkg) =>
        pkg.code === code
          ? { ...pkg, specs: [...(Array.isArray(pkg.specs) ? pkg.specs : []), ["", ""]] }
          : pkg,
      ),
    }));
    setDirtyPackages((current) => new Set([...current, code]));
  };

  const removeSpec = (code, index) => {
    setConfig((current) => ({
      ...current,
      packages: current.packages.map((pkg) =>
        pkg.code === code
          ? { ...pkg, specs: pkg.specs.filter((_, pairIndex) => pairIndex !== index) }
          : pkg,
      ),
    }));
    setDirtyPackages((current) => new Set([...current, code]));
  };

  const updateItem = (itemKey, key, value) => {
    setConfig((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.item_key === itemKey ? { ...item, [key]: value } : item,
      ),
    }));
    setDirtyItems((current) => new Set([...current, itemKey]));
  };

  const updateItemPrice = (item, packageCode, value) => {
    updateItem(item.item_key, "prices", { ...(item.prices || {}), [packageCode]: value });
  };

  const toggleItemPackage = (item, packageCode) => {
    const included = new Set(itemIncludedPackages(item));
    if (included.has(packageCode)) included.delete(packageCode);
    else included.add(packageCode);
    updateItem(item.item_key, "included_in", [...included]);
  };

  const uploadItemImage = async (item, file) => {
    if (!file) return;
    setUploadingItemKey(item.item_key);
    setMessage("");
    try {
      const result = await uploadQuoteToolItemImage(item.item_key, file);
      setConfig((current) => ({
        ...current,
        items: current.items.map((entry) =>
          entry.item_key === item.item_key ? { ...entry, image_url: result.image_url } : entry,
        ),
      }));
      // The image is already uploaded, but keep the usual Save changes state
      // consistent with every other item edit in this screen.
      setDirtyItems((current) => new Set([...current, item.item_key]));
      setMessage("Image saved for " + item.name + ".");
    } catch (error) {
      setMessage(error.message || "Unable to upload quotation item image.");
    } finally {
      setUploadingItemKey(null);
    }
  };

  const itemIncludedPackages = (item) =>
    Array.isArray(item.included_in)
      ? item.included_in
      : (DEFAULT_INCLUDED_BY_ITEM[item.item_key] || []);

  const updateSetting = (key, value) => {
    setConfig((current) => ({
      ...current,
      settings: { ...current.settings, [key]: value },
    }));
    setSettingsDirty(true);
  };

  const updateRoomAmount = (sectionKey, packageCode, value) => {
    const key = ROOM_AMOUNT_KEY(sectionKey, packageCode);
    setConfig((current) => ({
      ...current,
      settings: { ...current.settings, [key]: value },
    }));
    setDirtyRoomAmounts((current) => new Set([...current, key]));
  };

  const save = async () => {
    if (!changeCount) return;
    setSaving(true);
    setMessage("");
    try {
      await saveQuoteToolPricing({
        packages: config.packages
          .filter((pkg) => dirtyPackages.has(pkg.code))
          .map((pkg) => ({ ...pkg })),
        items: config.items
          .filter((item) => dirtyItems.has(item.item_key))
          .map((item) => ({
            ...item,
            price: Number(item.price),
            prices: Object.fromEntries(config.packages.map((pkg) => [pkg.code, Number(item.prices?.[pkg.code] ?? item.price)])),
            unit: item.unit || "EACH",
            included_in: itemIncludedPackages(item),
          })),
        settings: settingsDirty ? config.settings : undefined,
        roomAmounts: Object.fromEntries(
          [...dirtyRoomAmounts].map((key) => [key, config.settings[key] ?? ""]),
        ),
      });
      setDirtyPackages(new Set());
      setDirtyItems(new Set());
      setDirtyRoomAmounts(new Set());
      setSettingsDirty(false);
      setMessage("Pricing saved. The quotation tool will show these prices the next time its page loads.");
    } catch (error) {
      setMessage(error.message || "Unable to save pricing.");
    } finally {
      setSaving(false);
    }
  };

  const activeLabel = SECTIONS.find(([key]) => key === activeSection)?.[1];

  return (
    <main className="planner-master-page">
      <header className="planner-master-head">
        <div>
          <p>Dwellora Designs</p>
          <h1>Quotation Tool Pricing</h1>
          <span>
            Set package inclusions and item prices used by the website quotation tool.
          </span>
        </div>
        <div>
          <a href="/admin">Dashboard</a>
          <a href="/admin/catalogue">Furniture Planner pricing</a>
          <button
            type="button"
            onClick={() => {
              clearAdminSession();
              window.location.replace("/admin/login");
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {message && (
        <p className={`planner-message${message.includes("saved") ? "" : " planner-message--error"}`}>
          {message}
        </p>
      )}

      <section className="planner-help">
        <strong>How this works</strong>
        <span>
          Package totals are calculated from the items included in each package. Item prices are
          fixed prices per unit. Whole-room removal always deducts the included-item total.
          Items marked Hidden are not shown to customers. Changes appear
          in the quotation tool the next time its page loads. Quotations already sent are not changed.
        </span>
      </section>

      <nav className="planner-room-tabs" aria-label="Quotation tool sections">
        {SECTIONS.map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={activeSection === key ? "is-active" : ""}
            onClick={() => setActiveSection(key)}
          >
            {label}
          </button>
        ))}
      </nav>

      <section className="planner-rate-panel">
        <div className="planner-panel-title">
          <div>
            <p>Quotation tool</p>
            <h2>{activeLabel}</h2>
          </div>
          <button type="button" disabled={!changeCount || saving} onClick={save}>
            {saving ? "Saving…" : `Save ${changeCount || ""} change${changeCount === 1 ? "" : "s"}`}
          </button>
        </div>

        {loading && <p className="planner-state">Loading your pricing…</p>}

        {!loading && activeSection === "packages" && (
          <div className="qp-packages">
            {config.packages.map((pkg) => (
              <article className="qp-package" key={pkg.code}>
                <div className="qp-grid">
                  <label>
                    Package name
                    <input
                      type="text"
                      value={pkg.name}
                      onChange={(event) => updatePackage(pkg.code, "name", event.target.value)}
                    />
                  </label>
                  <label>
                    Tag line
                    <input
                      type="text"
                      value={pkg.tag}
                      onChange={(event) => updatePackage(pkg.code, "tag", event.target.value)}
                    />
                  </label>
                  <label>
                    Status
                    <select
                      value={pkg.status}
                      onChange={(event) => updatePackage(pkg.code, "status", event.target.value)}
                    >
                      <option value="active">Available to customers</option>
                      <option value="inactive">Hidden</option>
                    </select>
                  </label>
                  <label className="qp-check">
                    <input
                      type="checkbox"
                      checked={Boolean(pkg.popular)}
                      onChange={(event) => updatePackage(pkg.code, "popular", event.target.checked ? 1 : 0)}
                    />
                    Show as most popular
                  </label>
                </div>
                <h3>What this package includes</h3>
                {pkg.specs.map((pair, index) => (
                  <div className="qp-spec" key={`${pkg.code}-${index}`}>
                    <input
                      type="text"
                      aria-label="Specification title"
                      value={pair[0]}
                      onChange={(event) => updateSpec(pkg.code, index, 0, event.target.value)}
                    />
                    <input
                      type="text"
                      aria-label="Specification detail"
                      value={pair[1]}
                      onChange={(event) => updateSpec(pkg.code, index, 1, event.target.value)}
                    />
                    <button
                      type="button"
                      className="qp-spec__remove"
                      onClick={() => removeSpec(pkg.code, index)}
                      aria-label={`Remove ${pair[0] || "package inclusion"}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="qp-spec__add"
                  onClick={() => addSpec(pkg.code)}
                  disabled={pkg.specs.length >= 20}
                >
                  + Add package content
                </button>
                <p className="qp-total">
                  Customer total: calculated from this package's included item prices
                </p>
              </article>
            ))}
            {!config.packages.length && (
              <p className="planner-state">
                No packages found. Run quote-tool-pricing.sql once, then reload this page.
              </p>
            )}
          </div>
        )}

        {!loading && activeSection === "room-amounts" && (
          <section className="qp-room-amounts">
            <p>Whole-room removal uses the total of the included items automatically. These legacy values are retained only for old records and do not affect new quotations.</p>
            <div className="qp-room-amounts__table">
              <div className="qp-room-amounts__head">Room</div>
              {config.packages.map((pkg) => <div className="qp-room-amounts__head" key={pkg.code}>{pkg.name}</div>)}
              {ROOM_SECTIONS.map(([sectionKey, label]) => (
                <Fragment key={sectionKey}>
                  <strong>{label}</strong>
                  {config.packages.map((pkg) => {
                    const key = ROOM_AMOUNT_KEY(sectionKey, pkg.code);
                    return <label key={key}><span className="sr-only">{label} {pkg.name} amount</span><input type="number" min="0" step="0.01" placeholder="Item total" value={config.settings[key] ?? ""} onChange={(event) => updateRoomAmount(sectionKey, pkg.code, event.target.value)} /></label>;
                  })}
                </Fragment>
              ))}
            </div>
          </section>
        )}

        {!loading && !["packages", "room-amounts", "settings"].includes(activeSection) && (
          <section className="planner-rate-group">
            <div>
              {sectionItems.map((item) => (
                <article className="qp-item" key={item.item_key}>
                  <label className="qp-item__name">
                    Item
                    <input
                      type="text"
                      value={item.name}
                      onChange={(event) => updateItem(item.item_key, "name", event.target.value)}
                    />
                  </label>
                  <div className="qp-item__prices">
                    {config.packages.map((pkg) => (
                      <label key={pkg.code}>
                        {pkg.name} price (₹)
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.prices?.[pkg.code] ?? item.price ?? ""}
                          onChange={(event) => updateItemPrice(item, pkg.code, event.target.value)}
                        />
                      </label>
                    ))}
                  </div>
                  <label>
                    Status
                    <select
                      value={item.status}
                      onChange={(event) => updateItem(item.item_key, "status", event.target.value)}
                    >
                      <option value="active">Available</option>
                      <option value="inactive">Hidden</option>
                    </select>
                  </label>
                  <label>
                    Unit
                    <select
                      value={item.unit || "EACH"}
                      onChange={(event) => updateItem(item.item_key, "unit", event.target.value)}
                    >
                      {UNITS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {config.packages.some((pkg) => !itemIncludedPackages(item).includes(pkg.code)) && (
                    <div className="qp-item__image">
                      {item.image_url ? <img src={adminMediaUrl(item.image_url)} alt="" /> : <span>No image</span>}
                      <label>
                        {uploadingItemKey === item.item_key ? "Uploading…" : item.image_url ? "Change image" : "Upload image"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          disabled={uploadingItemKey === item.item_key}
                          onChange={(event) => {
                            uploadItemImage(item, event.target.files?.[0]);
                            event.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  )}
                  <fieldset className="qp-item__packages">
                    <legend>Customer option by package</legend>
                    {config.packages.map((pkg) => {
                      const included = itemIncludedPackages(item).includes(pkg.code);
                      return (
                        <label key={pkg.code} title={included ? "Included in this package; the customer can remove it." : "Optional add-on for this package; the customer can add it."}>
                          <input type="checkbox" checked={included} onChange={() => toggleItemPackage(item, pkg.code)} />
                          <span>{pkg.name}: <b>{included ? "Included" : "Add-on"}</b></span>
                        </label>
                      );
                    })}
                  </fieldset>
                  <div className="qp-item__total">
                    <span>Fallback price</span>
                    <strong>{money(item.price)} / {unitLabel(item.unit)}</strong>
                  </div>
                </article>
              ))}
              {!sectionItems.length && (
                <p className="planner-state">No items in this section yet.</p>
              )}
            </div>
          </section>
        )}

        {!loading && activeSection === "settings" && (
          <div className="planner-settings-grid">
            <label>
              Minimum carpet area (sq.ft)
              <input
                type="number"
                min="0"
                step="1"
                value={config.settings.min_area ?? ""}
                onChange={(event) => updateSetting("min_area", event.target.value)}
              />
            </label>
            <label>
              Quotation valid for (days)
              <input
                type="number"
                min="1"
                max="365"
                step="1"
                value={config.settings.validity_days ?? ""}
                onChange={(event) => updateSetting("validity_days", event.target.value)}
              />
            </label>
            <label className="qp-wide">
              GST note shown on the quotation
              <input
                type="text"
                maxLength="255"
                value={config.settings.gst_note ?? ""}
                onChange={(event) => updateSetting("gst_note", event.target.value)}
              />
            </label>
          </div>
        )}
      </section>
    </main>
  );
}
